import __ from "web/Layout/Translations";

import formatDate from "web/utils/data/parser/dateAndTime/formatDate";
import formatHours from "web/utils/data/parser/dateAndTime/formatHours";
import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import type {
  BankPartialDischargeItems,
  BankResetItems,
  IBankItem,
} from "web/types/Banks";
import type { IPaymentSettingsItem } from "web/types/Payment";
import type { Nullable } from "web/types/Utils";

import { BANK_PAYMENTS_TYPE } from "web/utils/system/redux/separateFinancingSourcesPerProduct/consts";
import type { TBankCountsForPaymentSortContext } from "web/utils/system/redux/separateFinancingSourcesPerProduct/getBankCountForPaymentSortContext";

import parsePaymentSettings, {
  type PaymentSettingsInput,
} from "./parsePaymentSettings";
import sortProductPointBanks from "./sortProductPointBanks";

export type ProductPointBankUnavailableReason =
  | "CATEGORY"
  | "BLOCKED"
  | null;

export interface IProductPointBankExpiryWarning {
  text: string;
  dateTime: string;
}

export interface IProductPointBankItem extends IBankItem {
  availableForProduct: boolean;
  unavailableReason: ProductPointBankUnavailableReason;
  unavailableReasonText: string | null;
  expiryWarning: IProductPointBankExpiryWarning | null;
}

export interface IProductPointBanksEligibility {
  banks: IProductPointBankItem[];
  available: IProductPointBankItem[];
  unavailable: IProductPointBankItem[];
  availableTotal: number;
}

export interface IGetProductPointBanksEligibilityOptions {
  paymentSuggestion?: Nullable<string>;
  bankCountsForPaymentSortContext?: TBankCountsForPaymentSortContext;
}

const isBankAllowedInPaymentSettings = (
  bank: IBankItem,
  paymentSettings: IPaymentSettingsItem[]
) =>
  paymentSettings.some(
    (payment) =>
      payment?.type === BANK_PAYMENTS_TYPE &&
      payment.active &&
      payment.code === bank.points_bank_id
  );

const getExpiryWarning = (
  bank: IBankItem,
  banksReset?: BankResetItems,
  banksDischarged?: BankPartialDischargeItems
): IProductPointBankExpiryWarning | null => {
  const discharge = banksDischarged?.find(
    (item) => item.points_bank_id === bank.points_bank_id
  );

  if (discharge?.amount && discharge?.discharge_date) {
    const date = formatDate(discharge.discharge_date);
    const time = formatHours(discharge.discharge_date);
    if (date && time) {
      return {
        text: `${discharge.amount} ${__("pkt")} ${__("straci ważność")}`,
        dateTime: `${date}, ${time}`,
      };
    }
  }

  const reset = banksReset?.find(
    (item) => item.pointsBankId === bank.points_bank_id
  );

  if (reset?.resetDateTime) {
    const date = formatDate(reset.resetDateTime);
    const time = formatHours(reset.resetDateTime);
    if (date && time) {
      return {
        text: __("Punkty stracą ważność"),
        dateTime: `${date}, ${time}`,
      };
    }
  }

  return null;
};

const getUnavailableReasonText = (
  reason: ProductPointBankUnavailableReason,
  blockadeEnd?: string
): string | null => {
  switch (reason) {
    case "CATEGORY":
      return __("Niedostępne dla tej kategorii produktu.");
    case "BLOCKED": {
      if (blockadeEnd) {
        const date = formatDate(blockadeEnd);
        const time = formatHours(blockadeEnd);
        if (date && time) {
          return `${__("Bank zablokowany do")} ${date} ${time}`;
        }
      }
      return __("Bank zablokowany");
    }
    default:
      return null;
  }
};

const getProductPointBanksEligibility = (
  banks: IBankItem[] | undefined,
  paymentSettings?: PaymentSettingsInput,
  banksReset?: BankResetItems,
  banksDischarged?: BankPartialDischargeItems,
  options: IGetProductPointBanksEligibilityOptions = {}
): IProductPointBanksEligibility => {
  const { paymentSuggestion = null, bankCountsForPaymentSortContext } =
    options;
  const activeBanks = isArrayHasItems(banks)
    ? banks.filter((bank) => bank && +bank.balance > 0)
    : [];
  const settings = parsePaymentSettings(paymentSettings);

  const mapped: IProductPointBankItem[] = activeBanks.map((bank) => {
    const allowedForProduct = isBankAllowedInPaymentSettings(bank, settings);
    const isBlocked = !!bank.blockade_locked;

    let unavailableReason: ProductPointBankUnavailableReason = null;
    if (isBlocked) {
      unavailableReason = "BLOCKED";
    } else if (!allowedForProduct) {
      unavailableReason = "CATEGORY";
    }

    const availableForProduct = allowedForProduct && !isBlocked;

    return {
      ...bank,
      availableForProduct,
      unavailableReason,
      unavailableReasonText: getUnavailableReasonText(
        unavailableReason,
        bank.blockade_time_blockade_end
      ),
      expiryWarning: availableForProduct
        ? getExpiryWarning(bank, banksReset, banksDischarged)
        : null,
    };
  });

  const sorted = sortProductPointBanks(mapped, {
    paymentSuggestion,
    bankResets: banksReset,
    bankCountsForPaymentSortContext,
  });

  const available = sorted.filter((bank) => bank.availableForProduct);
  const unavailable = sorted.filter((bank) => !bank.availableForProduct);
  const availableTotal = available.reduce(
    (sum, bank) => sum + Number(bank.balance),
    0
  );

  return {
    banks: sorted,
    available,
    unavailable,
    availableTotal,
  };
};

export default getProductPointBanksEligibility;
